/* eslint-disable camelcase */
import { TFilterOptions } from '@evinced-private/ui-common';

import omit from 'lodash-es/omit';
import { preprocessSearchValue } from 'src/apps/mobile-common/helpers/MobileSearchHelper';
import { buildSeverityFilterVariables } from 'src/apps/mobile-common/helpers/MobileSeverityFilterHelper';
import { IFilterOptions } from 'src/common/helpers/DataFilterHelper';
import { QueryVariablesBuilder } from 'src/common/helpers/GQLRequestsHelper/QueryVariablesBuilder';
import {
	ConditionOperators,
	Mfa_Session_RunsTextSearchFields
} from 'src/common/types/graphql.generated';

import { MfaUsageDataQueryVariables } from '../MfaUsageData.generated';

const DAYS_AMOUNT_FILTER_PARAM_KEY = 'daysAmountFilter';
const DAYS_AMOUNT_X2_FILTER_PARAM_KEY = 'daysAmountx2Filter';
const CREATED_BY_FILTER_PARAM_KEY = 'createdByFilter';
const CUSTOM_FIELDS_FILTER_PARAM_KEY = 'customFieldsFilter';

const sessionsSearchFields = [
	Mfa_Session_RunsTextSearchFields.SessionName,
	Mfa_Session_RunsTextSearchFields.App,
	Mfa_Session_RunsTextSearchFields.AppVersion,
	Mfa_Session_RunsTextSearchFields.Os,
	Mfa_Session_RunsTextSearchFields.CustomFields,
	Mfa_Session_RunsTextSearchFields.DeviceManufacturer,
	Mfa_Session_RunsTextSearchFields.DeviceModel,
	Mfa_Session_RunsTextSearchFields.DeviceType,
	Mfa_Session_RunsTextSearchFields.OsVersion,
	Mfa_Session_RunsTextSearchFields.AppBuildNumber,
	Mfa_Session_RunsTextSearchFields.ProductVersion
];

export const buildMfaUsageDataVariables = (filters: IFilterOptions): MfaUsageDataQueryVariables => {
	const variablesBuilder = new QueryVariablesBuilder<MfaUsageDataQueryVariables>({
		searchFields: sessionsSearchFields
	});

	const { searchValue, sessionsPeriod, owner } = filters;
	const sessionsSinglePeriod = sessionsPeriod?.[0];

	// omit all the known filter keys.
	// this will leave us with the dynamic filter keys - the ones configured in the CC for labels
	const dynamicFilterOptions: TFilterOptions = omit(filters ?? {}, [
		'owner',
		'sessionsPeriod',
		'searchValue',
		'platform',
		'appName',
		'versions',
		'severity'
	]);

	const searchValues = preprocessSearchValue(searchValue);

	variablesBuilder
		.addTextSearchParam(searchValues)
		.addFilterParam(CREATED_BY_FILTER_PARAM_KEY, owner)
		.addTupleFilterParam(
			CUSTOM_FIELDS_FILTER_PARAM_KEY,
			dynamicFilterOptions,
			ConditionOperators.All
		);

	// Add dedicated filter fields
	if (filters?.platform?.length) {
		variablesBuilder.addFilterParam('osFilter', filters.platform);
	}

	if (filters?.appName?.length) {
		variablesBuilder.addFilterParam('appFilter', filters.appName);
	}

	if (filters?.versions?.length) {
		variablesBuilder.addFilterParam('appVersionFilter', filters.versions);
	}

	if (
		sessionsSinglePeriod?.value &&
		sessionsSinglePeriod.value !== 'all' &&
		typeof sessionsSinglePeriod.value === 'number'
	) {
		variablesBuilder
			.addConditionParam(DAYS_AMOUNT_FILTER_PARAM_KEY, sessionsSinglePeriod.value)
			.addConditionParam(DAYS_AMOUNT_X2_FILTER_PARAM_KEY, 2 * sessionsSinglePeriod.value);
	}

	const variables = variablesBuilder.getVariables();
	const severityFilterVariables = buildSeverityFilterVariables<MfaUsageDataQueryVariables>(
		filters?.severity
	);

	return { ...variables, ...severityFilterVariables };
};
